const APP_NAME = "S-Process";

const toTitleCase = (value: string): string =>
  value
    .replace(/[-_]+/g, " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());

const decodeSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

const normalizePath = (path: string): string => {
  const [withoutHash = ""] = path.split("#", 1);
  const [withoutQuery = ""] = withoutHash.split("?", 1);
  if (withoutQuery === "/") {
    return "/";
  }
  return withoutQuery.replace(/\/+$/, "");
};

const formatRoundScopedTitle = (
  baseLabel: string,
  subPath: string[],
): string => {
  if (subPath.length >= 2 && subPath[0] === "round") {
    const roundId = subPath[1] ?? "";
    if (subPath.length >= 4 && subPath[2] === "proposal") {
      return `${baseLabel} (Proposal ${subPath[3] ?? ""})`;
    }
    if (subPath.length >= 4 && subPath[2] === "transcript") {
      return `${baseLabel} (Transcript ${subPath[3] ?? ""})`;
    }
    return `${baseLabel} (Round ${roundId})`;
  }
  return baseLabel;
};

const getAdminTitle = (pathSegments: string[]): string => {
  const adminSegments = pathSegments.slice(1);
  if (adminSegments.length === 0) {
    return "Admin Tools";
  }

  const [section, ...subPath] = adminSegments;
  switch (section) {
    case "evaluator-scoring":
    case "recommender-notes":
      return formatRoundScopedTitle("Recommender Notes", subPath);
    case "evaluator-analysis":
    case "notes-analysis":
      return formatRoundScopedTitle("Notes Analysis", subPath);
    case "transcripts":
      return formatRoundScopedTitle("Transcripts", subPath);
    case "llm-note-scoring-calibration":
      return "LLM Note Scoring Calibration";
    case "note-scoring-lab":
      return formatRoundScopedTitle("Note Scoring Lab", subPath);
    case "llm-extractions":
    case "proposal-application-summary":
      return formatRoundScopedTitle("Proposal Application Summary", subPath);
    case "key-people-extraction":
      return formatRoundScopedTitle("Key People Extraction", subPath);
    case "key-people-deep-research":
      return formatRoundScopedTitle("Key People Deep Research", subPath);
    case "mvf-quality":
      return "MVF Quality";
    case "fact-checking":
      return formatRoundScopedTitle("Fact Checking", subPath);
    case "sentiment-analysis":
      return formatRoundScopedTitle("Sentiment Analysis", subPath);
    case "proposal-summaries":
      return formatRoundScopedTitle("Proposal Summaries", subPath);
    case "recommender-summaries":
      return formatRoundScopedTitle("Recommender Summaries", subPath);
    case "proposal-deep-research":
      return formatRoundScopedTitle("Proposal Deep Research", subPath);
    case "prompt-lab":
      return formatRoundScopedTitle("Prompt Lab", subPath);
    default:
      return "Admin Tools";
  }
};

const getRoundAdminTitle = (adminSegments: string[]): string => {
  if (adminSegments.length === 0) {
    return "Round Admin";
  }

  const [section, ...rest] = adminSegments;
  switch (section) {
    case "track-settings":
      return rest[0] != null
        ? `Track Settings (${toTitleCase(rest[0])})`
        : "Track Settings";
    case "round-members":
      return "Round Members";
    case "recommender-activity":
      return "Recommender Activity";
    case "fact-check-responses":
      return "Fact Check Responses";
    case "proposal-forms":
      return "Proposal Forms";
    case "data-syncing":
      return "Data Syncing";
    case "anonymization":
      return "Name Visibility";
    case "logs":
      return "Logs";
    case "impersonate":
      return "Impersonate";
    case "proposals-viewer":
      return "Proposals";
    default:
      return "Round Admin";
  }
};

const getRoundSectionTitle = (
  section: string | undefined,
  detail: string[],
): string => {
  switch (section) {
    case undefined:
      return "Round";
    case "applications":
      if (detail[0] === "builder") return "Application Builder";
      if (detail[0] === "settings") return "Applications: Settings";
      return detail[0] === "forms"
        ? "Applications: Forms"
        : "Applications: Responses";
    case "proposals":
      return detail[0] != null
        ? `${decodeSegment(detail[0])} (Proposal)`
        : "Proposals";
    case "comparisons":
      return detail[0] != null
        ? `${toTitleCase(decodeSegment(detail[0]))}'s MVFs`
        : "My MVFs";
    case "disagreements":
      return "Disagreements";
    case "sankey":
      return "Funding Flow";
    case "funding-flow":
      return "Funding Flow";
    case "funder":
      return "Funder Workspace";
    case "members":
      return "Members";
    case "analysis":
      switch (detail[0]) {
        case undefined:
          return "Analysis";
        case "comparisons":
          return "Analysis: Comparisons";
        case "disagreements":
          return "Analysis: Disagreements";
        case "sankey":
          return "Analysis: Funding Flow";
        // URL segments keep the old "accountables"/"guarantees" names; the
        // user-facing terms are now "Contributions"/"Endorsement Levels".
        case "funders-accountables":
          return "Analysis: Funder Contributions";
        case "funders-guarantees":
          return "Analysis: Funder Endorsement Levels";
        case "recommenders-accountables":
          return "Analysis: Recommender Contributions";
        case "recommenders-guarantees":
          return "Analysis: Recommender Endorsement Levels";
        default:
          return "Analysis";
      }
    case "settings":
      switch (detail[0]) {
        case undefined:
          return "Settings";
        case "users":
          return "Users";
        case "form":
          return "Proposal Form";
        case "importAndExport":
          return "Import and Export";
        case "round":
          return "Round Settings";
        case "funder":
          return "Funder Settings";
        default:
          return "Settings";
      }
    case "admin":
      return getRoundAdminTitle(detail);
    case "export-data":
      return "Export Data";
    case "speculation-grants":
      return "Speculation Grants";
    case "onboarding":
      return "Onboarding";
    case "investigations":
      return "Investigations";
    case "recusals":
      return detail[0] != null
        ? `Recusals (${toTitleCase(detail[0])})`
        : "Recusals";
    default:
      return "Round";
  }
};

const getRoundTitle = (pathSegments: string[]): string => {
  const roundId = pathSegments[1] ?? "";
  if (roundId.length === 0) {
    return "Round";
  }

  return `${getRoundSectionTitle(pathSegments[2], pathSegments.slice(3))} (Round ${roundId})`;
};

const getPageTitle = (path: string): string => {
  const normalizedPath = normalizePath(path);
  if (normalizedPath === "/") {
    return "Home";
  }

  const pathSegments = normalizedPath.split("/").filter(Boolean);
  if (pathSegments.length === 0) {
    return "Home";
  }

  switch (pathSegments[0]) {
    case "profile":
      return "Profile";
    case "applicant":
      return "Your Applications";
    case "dev":
      return "Dev";
    case "team-invitations":
      return "Team Invitation";
    case "teams":
      return "Teams";
    case "application-builder":
      return "Application Builder";
    case "apply":
      return "Application Form";
    case "signout":
      return "Sign Out";
    case "admin":
      return getAdminTitle(pathSegments);
    case "round":
      return getRoundTitle(pathSegments);
    default:
      return "Page Not Found";
  }
};

export const getDocumentTitleForPath = (path: string): string =>
  `${getPageTitle(path)} · ${APP_NAME}`;

export const getDocumentTitleForRoundSubPath = (
  path: string,
  roundLabel: string,
): string => {
  const normalizedPath = normalizePath(path);
  const roundSubPathSegments = normalizedPath.split("/").filter(Boolean);
  const sectionTitle = getRoundSectionTitle(
    roundSubPathSegments[0],
    roundSubPathSegments.slice(1),
  );

  return `${sectionTitle} (${roundLabel}) · ${APP_NAME}`;
};
